import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { userGameRewards } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";
import { useAudio } from "../../../Providers/AudioProvider";
import "./puzzle.css";
import task from "../Task";
function Puzzle() {
  const [board, setBoard] = useState([]);
  const [isWon, setIsWon] = useState(false);
  const [moves, setMoves] = useState(0);
  const [gridSize, setGridSize] = useState(3);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [shuffledImageUrls, setShuffledImageUrls] = useState([]);
  const [difficulty, setDifficulty] = useState("Easy");
  const [easyGamesPlayed, setEasyGamesPlayed] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const playClick = useAudio();
  const [touchStart, setTouchStart] = useState({ x: null, y: null });
  const [touchEnd, setTouchEnd] = useState({ x: null, y: null });

  // Original image URLs
  const imageUrls = [
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m1.jpg?random=1",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m2.jpg?random=2",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m3.jpg?random=3",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m4.jpg?random=4",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m5.jpg?random=5",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m6.jpg?random=6",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m7.jpg?random=7",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m8.jpg?random=8",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/m9.jpg?random=9",
  ];

  const scrollableRef = useRef(null);
  useEffect(() => {
    const preventTouchScroll = (event) => {
      // Allow scrolling only for the scrollable div
      // Add null check to prevent runtime errors
      if (
        scrollableRef.current &&
        !scrollableRef.current.contains(event.target)
      ) {
        event.preventDefault();
      }
    };
    // Add the event listener
    document.addEventListener("touchmove", preventTouchScroll, {
      passive: false,
    });
    // Cleanup the event listener
    return () => {
      document.removeEventListener("touchmove", preventTouchScroll);
    };
  }, []);

  useEffect(() => {
    // Shuffle the image URLs array when component mounts
    const shuffled = [...imageUrls];
    shuffleArray(shuffled);
    setShuffledImageUrls(shuffled);

    // Ensure shuffledImageUrls is set before initializing board
    setTimeout(() => {
      initializeBoard();
    }, 100);

    // Load high score from localStorage if available
    const savedHighScore = localStorage.getItem("puzzleHighScore");
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore));
    }

    // Load easy games played count from localStorage
    const today = new Date().toDateString();
    const storedData = localStorage.getItem("puzzleEasyGames");
    if (storedData) {
      const { date, count } = JSON.parse(storedData);
      if (date === today) {
        setEasyGamesPlayed(count);
        if (count >= 3) {
          setDifficulty("Medium");
        }
      } else {
        // Reset count for new day
        localStorage.setItem(
          "puzzleEasyGames",
          JSON.stringify({ date: today, count: 0 })
        );
        setEasyGamesPlayed(0);
      }
    } else {
      localStorage.setItem(
        "puzzleEasyGames",
        JSON.stringify({ date: today, count: 0 })
      );
    }
  }, []);

  // Image preloading effect
  useEffect(() => {
    const preloadImages = async () => {
      try {
        const imagePromises = imageUrls.map((url) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = url;

            img.onload = () => {
              resolve();
            };

            img.onerror = () => {
              reject(new Error(`Failed to load image: ${url}`));
            };
          });
        });

        await Promise.all(imagePromises);
        setImagesLoaded(true);
      } catch (error) {
        console.error("Error loading images:", error);
        setImageLoadError(true);
      }
    };

    preloadImages();
  }, []);

  const closePopUp = () => {
    setIsWon(false);
    // Load next image with shuffled tiles after closing popup
    getNextImage();
  };

  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  const getNextImage = () => {
    // Move to next image in shuffled array
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % shuffledImageUrls.length
    );
    // Initialize the board with shuffled tiles for the new image
    const totalTiles = gridSize * gridSize - 1;
    const numbers = Array.from({ length: totalTiles }, (_, i) => i);
    numbers.push(null);
    shuffleArray(numbers);
    setBoard(numbers);
    setMoves(0);
    setIsWon(false);
  };

  useEffect(() => {
    logPageView("Puzzle ", "Puzzle game");
  }, []);

  const getShuffleCount = () => {
    switch (difficulty) {
      case "Easy":
        return 20; // Fewer shuffles for easier puzzles
      case "Hard":
        return 200; // More shuffles for harder puzzles
      default:
        return 100; // Medium difficulty
    }
  };

  const shuffleArray = (array) => {
    const shuffleCount = getShuffleCount();
    for (let i = 0; i < shuffleCount; i++) {
      const emptyIndex = array.indexOf(null);
      const possibleMoves = [];

      // Find all possible moves
      if (emptyIndex % gridSize > 0) possibleMoves.push(emptyIndex - 1); // Left
      if (emptyIndex % gridSize < gridSize - 1)
        possibleMoves.push(emptyIndex + 1); // Right
      if (emptyIndex >= gridSize) possibleMoves.push(emptyIndex - gridSize); // Up
      if (emptyIndex < gridSize * (gridSize - 1))
        possibleMoves.push(emptyIndex + gridSize); // Down

      // Randomly select one of the possible moves
      const randomMove =
        possibleMoves[Math.floor(Math.random() * possibleMoves.length)];

      // Swap the empty space with the selected tile
      [array[emptyIndex], array[randomMove]] = [
        array[randomMove],
        array[emptyIndex],
      ];
    }
  };

  const initializeBoard = () => {
    const totalTiles = gridSize * gridSize - 1;
    const numbers = Array.from({ length: totalTiles }, (_, i) => i);
    numbers.push(null);

    // Start with a solved board and shuffle it based on difficulty
    shuffleArray(numbers);
    setBoard(numbers);
    setMoves(0);
    setIsWon(false);
  };

  const handleTouchStart = (e) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
    setTouchEnd({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
  };

  const handleTouchMove = (e) => {
    setTouchEnd({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
  };

  const moveTile = (index, emptyIndex) => {
    const newBoard = [...board];
    [newBoard[index], newBoard[emptyIndex]] = [
      newBoard[emptyIndex],
      newBoard[index],
    ];

    // Check if this move will complete the game
    const totalTiles = gridSize * gridSize - 1;
    const winningBoard = [...Array(totalTiles)].map((_, i) => i).concat(null);
    const willWin = JSON.stringify(newBoard) === JSON.stringify(winningBoard);

    setBoard(newBoard);
    // Only increment moves if this move doesn't complete the game
    if (!willWin) {
      setMoves((prevMoves) => prevMoves + 1);
    }
    checkWin(newBoard);
  };

  const handleTouchEnd = (index) => {
    if (!touchStart.x || !touchStart.y || !touchEnd.x || !touchEnd.y) return;

    const deltaX = touchEnd.x - touchStart.x;
    const deltaY = touchEnd.y - touchStart.y;
    const minSwipeDistance = 30; // Minimum distance for a swipe to be registered
    const emptyIndex = board.indexOf(null);
    const currentRow = Math.floor(index / gridSize);
    const emptyRow = Math.floor(emptyIndex / gridSize);
    const currentCol = index % gridSize;
    const emptyCol = emptyIndex % gridSize;

    // Check if it's a tap (very small movement)
    if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
      // For tap, check if the empty space is adjacent in any direction
      if (
        (currentRow === emptyRow && Math.abs(emptyCol - currentCol) === 1) ||
        (currentCol === emptyCol && Math.abs(emptyRow - currentRow) === 1)
      ) {
        moveTile(index, emptyIndex);
      }
      return;
    }

    // Handle swipe movements
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe
      if (Math.abs(deltaX) < minSwipeDistance) return;

      const direction = deltaX > 0 ? 1 : -1; // 1 for right, -1 for left

      // Check if the empty space is in the same row and exactly one step away
      if (currentRow === emptyRow && Math.abs(emptyCol - currentCol) === 1) {
        // Check if the empty space is in the swipe direction
        if (
          (direction === 1 && emptyCol === currentCol + 1) ||
          (direction === -1 && emptyCol === currentCol - 1)
        ) {
          moveTile(index, emptyIndex);
        }
      }
    } else {
      // Vertical swipe
      if (Math.abs(deltaY) < minSwipeDistance) return;

      const direction = deltaY > 0 ? 1 : -1; // 1 for down, -1 for up

      // Check if the empty space is in the same column and exactly one step away
      if (currentCol === emptyCol && Math.abs(emptyRow - currentRow) === 1) {
        // Check if the empty space is in the swipe direction
        if (
          (direction === 1 && emptyRow === currentRow + 1) ||
          (direction === -1 && emptyRow === currentRow - 1)
        ) {
          moveTile(index, emptyIndex);
        }
      }
    }

    setTouchStart({ x: null, y: null });
    setTouchEnd({ x: null, y: null });
  };

  const handleTileClick = (index) => {
    const emptyIndex = board.indexOf(null);
    if (isAdjacent(index, emptyIndex)) {
      const newBoard = [...board];
      [newBoard[index], newBoard[emptyIndex]] = [
        newBoard[emptyIndex],
        newBoard[index],
      ];
      setBoard(newBoard);
      setMoves(moves + 1);
      checkWin(newBoard);
    }
  };

  const isAdjacent = (index1, index2) => {
    const row1 = Math.floor(index1 / gridSize);
    const col1 = index1 % gridSize;
    const row2 = Math.floor(index2 / gridSize);
    const col2 = index2 % gridSize;
    return Math.abs(row1 - row2) + Math.abs(col1 - col2) === 1;
  };

  const calculateScore = (moves) => {
    // Base score is 1000
    // Each move reduces score by points based on difficulty
    let pointsPerMove;
    switch (difficulty) {
      case "Easy":
        pointsPerMove = 100; // Less penalty for moves in easy mode
        break;
      case "Hard":
        pointsPerMove = 10; // More penalty for moves in hard mode
        break;
      default:
        pointsPerMove = 50; // Medium difficulty
    }

    const calculatedScore = Math.max(100, 1000 - moves * pointsPerMove);
    return calculatedScore;
  };

  const checkWin = (currentBoard) => {
    const totalTiles = gridSize * gridSize - 1;
    const winningBoard = [...Array(totalTiles)].map((_, i) => i).concat(null);
    const isWinning =
      JSON.stringify(currentBoard) === JSON.stringify(winningBoard);

    if (isWinning) {
      const newScore = calculateScore(moves);
      setScore(newScore);
      updateEasyGamesPlayed(); // Update easy games played count on win

      // Check if we need to switch to medium difficulty
      if (difficulty === "Easy" && easyGamesPlayed >= 2) {
        // 2 because this is the third win
        setDifficulty("Medium");
        // Reinitialize the board with new difficulty
        const totalTiles = gridSize * gridSize - 1;
        const numbers = Array.from({ length: totalTiles }, (_, i) => i);
        numbers.push(null);
        shuffleArray(numbers);
        setBoard(numbers);
        setMoves(0);
      }

      // Update high score if current score is higher
      if (newScore > highScore) {
        setHighScore(newScore);
        localStorage.setItem("puzzleHighScore", newScore.toString());
      }
      // Store score in live API
      handleApiResponse(newScore);
    }
  };

  const handleApiResponse = async (points) => {
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "slide puzzle",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        // Only set isWon to true after successful API response
        setIsWon(true);
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  // Add loading overlay for API call
  const renderLoadingOverlay = () => {
    if (userDetails?.isLoading) {
      return (
        <div
          className="loading-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 9999,
          }}
        >
          <div
            className="loading-spinner"
            style={{
              width: "25px",
              height: "25px",
              border: "2px solid #121212",
              borderTop: "2px solid rgb(52, 219, 119)",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
        </div>
      );
    }
    return null;
  };

  const getTileStyle = (index) => {
    if (index === null) return {};

    const tileSize = 100 / gridSize;
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;

    // Ensure we have a valid image URL with fallback
    const imageUrl =
      shuffledImageUrls[currentImageIndex] ||
      imageUrls[currentImageIndex] ||
      imageUrls[0];

    return {
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: `${gridSize * 100}%`,
      backgroundPosition: `${col * tileSize}% ${row * tileSize}%`,
      position: "relative",
      opacity: imagesLoaded ? 1 : 0,
      transition: "opacity 0.3s ease-in-out",
    };
  };

  const handleDifficultyChange = (e) => {
    const selectedDifficulty = e.target.value;
    let newDifficulty = selectedDifficulty;

    // If trying to select Easy when limit reached, force Medium
    if (selectedDifficulty === "Easy" && easyGamesPlayed >= 3) {
      newDifficulty = "Medium";
    }

    // Set the new difficulty
    setDifficulty(newDifficulty);

    // Reset game state
    setMoves(0);
    setIsWon(false);

    // Create and shuffle new board with new difficulty
    const totalTiles = gridSize * gridSize - 1;
    const numbers = Array.from({ length: totalTiles }, (_, i) => i);
    numbers.push(null);

    // Shuffle with new difficulty settings
    const shuffleCount = getShuffleCount();
    for (let i = 0; i < shuffleCount; i++) {
      const emptyIndex = numbers.indexOf(null);
      const possibleMoves = [];

      // Find all possible moves
      if (emptyIndex % gridSize > 0) possibleMoves.push(emptyIndex - 1); // Left
      if (emptyIndex % gridSize < gridSize - 1)
        possibleMoves.push(emptyIndex + 1); // Right
      if (emptyIndex >= gridSize) possibleMoves.push(emptyIndex - gridSize); // Up
      if (emptyIndex < gridSize * (gridSize - 1))
        possibleMoves.push(emptyIndex + gridSize); // Down

      // Randomly select one of the possible moves
      const randomMove =
        possibleMoves[Math.floor(Math.random() * possibleMoves.length)];

      // Swap the empty space with the selected tile
      [numbers[emptyIndex], numbers[randomMove]] = [
        numbers[randomMove],
        numbers[emptyIndex],
      ];
    }

    setBoard(numbers);
  };

  const updateEasyGamesPlayed = () => {
    if (difficulty === "Easy") {
      const newCount = easyGamesPlayed + 1;
      setEasyGamesPlayed(newCount);
      const today = new Date().toDateString();
      localStorage.setItem(
        "puzzleEasyGames",
        JSON.stringify({ date: today, count: newCount })
      );
    }
  };

  // Loading state
  if (!imagesLoaded || shuffledImageUrls.length === 0) {
    return (
      <div
        className="loading-container"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          zIndex: 1000,
        }}
      >
        <div
          className="loading-spinner"
          style={{
            width: "50px",
            height: "50px",
            border: "5px solid #f3f3f3",
            borderTop: "5px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        <p style={{ color: "white", marginTop: "20px" }}>
          Loading puzzle images...
        </p>
      </div>
    );
  }

  // Error state
  if (imageLoadError) {
    return (
      <div
        className="error-container"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          zIndex: 1000,
        }}
      >
        <p style={{ color: "white", marginBottom: "20px" }}>
          Failed to load puzzle images. Please try again.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 20px",
            backgroundColor: "#3498db",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container py-2" style={{ pointerEvents: "fill" }}>
      {renderLoadingOverlay()}
      <img
        onClick={() => {
          toogleMenu(task, "task");
        }}
        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
        className="cancel-full "
        style={{ cursor: "pointer" }}
      />
      <button
        className="btn-help-puzzle"
        onClick={() => {
          playClick();
          setShowInstructionsModal(true);
        }}
        style={{
          position: "fixed",
          top: "5px",
          right: "5px",
          zIndex: 1000,
          background: "rgba(0, 0, 0, 0.7)",
          // border: "2px solid #fff",
          borderRadius: "50%",
          width: "30px",
          height: "30px",
          color: "white",
          fontSize: "20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="bi bi-question-circle"></i>
      </button>
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8">
          <div className="card shadow w-100">
            <div className="card-header text-white">
              <h2 className="text-center text-color mb-0 text-uppercase pb-2 welcome-text">
                Sliding Puzzle
              </h2>
            </div>
            <div className="card-body" style={{ padding: "0.5rem" }}>
              <div className="row">
                <div className="col-12">
                  <div className="align-items-center mb-3">
                    <div className="d-flex justify-content-between  align-items-center gap-2">
                      <span className="moves" style={{ width: "50%" }}>
                        Moves: {moves}
                      </span>

                      <span className="text-white">LEVEL:</span>
                      <select
                        className="form-select form-select-sm"
                        value={difficulty}
                        onChange={handleDifficultyChange}
                        style={{ width: "50%" }}
                      >
                        <option value="Easy" disabled={easyGamesPlayed >= 3}>
                          Easy{easyGamesPlayed >= 3 ? "(Limit Reached)" : ""}
                        </option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </select>
                    </div>
                  </div>

                  {/* Easy Mode Counter */}
                  {difficulty === "Easy" && easyGamesPlayed < 3 && (
                    <div className="text-center mb-2">
                      <small className="text-warning text-uppercase">
                        <i className="bi bi-info-circle me-1"></i>
                        Easy Mode: {easyGamesPlayed}/3 games played today
                        <br />
                        <span className="text-light text-uppercase">
                          {3 - easyGamesPlayed} games remaining
                        </span>
                      </small>
                    </div>
                  )}

                  {easyGamesPlayed >= 3 && (
                    <div className="text-center mb-2">
                      <small className="text-danger">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Easy Mode limit reached (3/3 games)
                        <br />
                        <span className="text-light">
                          Switch to Medium or Hard difficulty
                        </span>
                      </small>
                    </div>
                  )}

                  <div className="position-relative">
                    <div
                      className="puzzle-board"
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                        gap: "3px",
                        backgroundColor: "rgb(55 55 55)",
                        padding: "5px",
                        borderRadius: "5px",
                      }}
                    >
                      {board.map((tile, index) => (
                        <div
                          key={index}
                          onTouchStart={handleTouchStart}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={() => handleTouchEnd(index)}
                          className={`
                            d-flex align-items-center justify-content-center
                            ${tile !== null ? "shadow-sm" : ""}
                          `}
                          style={{
                            aspectRatio: "1",
                            cursor: tile !== null ? "pointer" : "default",
                            borderRadius: "4px",
                            transition: "all 0.2s ease",
                            backgroundColor: tile === null ? "#6c757d" : "#fff",
                            ...getTileStyle(tile),
                          }}
                        >
                          {tile !== null && (
                            <span
                              style={{
                                position: "absolute",
                                top: "5px",
                                right: "5px",
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              {tile + 1}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="d-flex justify-content-center align-items-center mb-3 mt-3">
                      <button
                        className="text-uppercase control-btn text-center"
                        onClick={initializeBoard}
                      >
                        New Game
                      </button>
                    </div>
                    <div className="text-center pt-1" ref={scrollableRef}>
                      <small
                        style={{ display: "block" }}
                        className="text-grey text-uppercase text-center w-100"
                      >
                        Slide/Click on tiles adjacent to the empty space to move
                        them.
                      </small>
                    </div>
                    {isWon && (
                      <div className="overlay">
                        <div className="full-popup">
                          <div className="popup-content">
                            <h2 className="epic">GAME OVER!</h2>
                            <img
                              src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                              className="cancel-img"
                              onClick={closePopUp}
                            />
                            <div className="row text-center">
                              <div className="col-12">
                                <div className="epic-div">
                                  <p className="pointsLabel">TOKENS EARNED</p>
                                  <div className="pointsValue1">
                                    <img
                                      src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                                      alt="logo"
                                    />
                                    <p className="pointsNumber">{score}</p>
                                  </div>
                                </div>
                                <p className="bottom-text">
                                  Keep Playing, get Tokens and chase the moves
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Modal */}
      {showInstructionsModal && (
        <div
          className="instructions-modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="instructions-modal1"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "black",
              borderRadius: "5px",
              padding: "20px",
              maxWidth: "500px",
              width: "95%",
              // maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              className="instructions-modal-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                className="text-uppercase"
                style={{ margin: 0, color: "#1bce1c" }}
              >
                Instructions
              </h3>
              <button
                onClick={() => {
                  playClick();
                  setShowInstructionsModal(false);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                ×
              </button>
            </div>

            <div className="instructions-modal-body text-uppercase f-size-14">
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <i
                    className="bi bi-arrow-left-right"
                    style={{
                      marginRight: "10px",
                      fontSize: "20px",
                      color: "#1bce1c",
                    }}
                  ></i>
                  <div style={{ color: "#fff" }}>
                    <strong>Objective:</strong> Rearrange the puzzle pieces to
                    complete the image
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "20px", color: "#fff" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <i
                    className="bi bi-hand-index"
                    style={{
                      marginRight: "10px",
                      fontSize: "20px",
                      color: "#1bce1c",
                    }}
                  ></i>
                  <div>
                    <strong>How to Move:</strong> Click or slide on tiles
                    adjacent to the empty space
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "20px", color: "#fff" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <i
                    className="bi bi-trophy"
                    style={{
                      marginRight: "10px",
                      fontSize: "20px",
                      color: "#1bce1c",
                    }}
                  ></i>
                  <div>
                    <strong>Scoring:</strong> Complete with fewer moves for a
                    higher score!
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "20px", color: "#fff" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <i
                    className="bi bi-gear"
                    style={{
                      marginRight: "10px",
                      fontSize: "20px",
                      color: "#1bce1c",
                    }}
                  ></i>
                  <div>
                    <strong>Difficulty:</strong> Choose Easy (3x3), Medium
                    (4x4), or Hard (5x5) grid
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "20px", color: "#fff" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <i
                    className="bi bi-clock"
                    style={{
                      marginRight: "10px",
                      fontSize: "20px",
                      color: "#1bce1c",
                    }}
                  ></i>
                  <div className="text-uppercase">
                    <strong>Easy Mode Limit:</strong> You can play 3 Easy games
                    per day
                    <br />
                    <small className="text-uppercase" style={{ color: "#ccc" }}>
                      After 3 games, you'll need to switch to Medium or Hard
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* <div
              className="instructions-modal-footer"
              style={{ textAlign: "center", marginTop: "20px", color: "#fff" }}
            >
              <button
                onClick={() => {
                  playClick();
                  setShowInstructionsModal(false);
                }}
                style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
              >
                Got it!
              </button>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
}

export default Puzzle;
